import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Checkbox,
    FormControlLabel,
    Select,
    MenuItem,
    Stack,
    Chip,
    Divider,
} from '@mui/material';
import { checkParamValue } from '../../mavlink/mavlinkHelper';
import { mavlink20 } from '../../mavlink/mavlink20Wrapper';

const getDefaultRangeByParamType = (paramType) => {
    switch (paramType) {
        case mavlink20.MAV_PARAM_TYPE_UINT8:
            return { min: 0, max: 255, step: 1 };
        case mavlink20.MAV_PARAM_TYPE_INT8:
            return { min: -128, max: 127, step: 1 };
        case mavlink20.MAV_PARAM_TYPE_UINT16:
            return { min: 0, max: 65535, step: 1 };
        case mavlink20.MAV_PARAM_TYPE_INT16:
            return { min: -32768, max: 32767, step: 1 };
        case mavlink20.MAV_PARAM_TYPE_UINT32:
            return { min: 0, max: 4294967295, step: 1 };
        case mavlink20.MAV_PARAM_TYPE_INT32:
            return { min: -2147483648, max: 2147483647, step: 1 };
        case mavlink20.MAV_PARAM_TYPE_UINT64:
            return { min: 0, max: Number.MAX_SAFE_INTEGER, step: 1 };
        case mavlink20.MAV_PARAM_TYPE_INT64:
            return { min: Number.MIN_SAFE_INTEGER, max: Number.MAX_SAFE_INTEGER, step: 1 };
        case mavlink20.MAV_PARAM_TYPE_REAL32:
        case mavlink20.MAV_PARAM_TYPE_REAL64:
            return { min: -Number.MAX_VALUE, max: Number.MAX_VALUE, step: 0.01 };
        default:
            return { min: Number.MIN_SAFE_INTEGER, max: Number.MAX_SAFE_INTEGER, step: 1 };
    }
};

const MetaRow = ({ label, value }) => (
    <Stack direction="row" spacing={1} sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 64 }}>
            {label}
        </Typography>
        <Typography
            variant="caption"
            sx={{ fontFamily: 'monospace', color: 'text.primary', wordBreak: 'break-word' }}
        >
            {value}
        </Typography>
    </Stack>
);

const ParameterValueEditor = ({
    selectedMavId,
    selectedParam,
    params,
    paramValue,
    setParamValue,
}) => {
    const [error, setError] = useState(false);
    const [helperText, setHelperText] = useState('');
    const [manualInputValue, setManualInputValue] = useState('');
    const [paramMetadataValue, setParamMetadataValue] = useState({});
    const [isBitmaskValue, setIsBitmaskValue] = useState(false);
    const [isSelectValue, setIsSelectValue] = useState(false);
    const [isNormalValue, setIsNormalValue] = useState(false);
    const [minValue, setMinValue] = useState(0);
    const [maxValue, setMaxValue] = useState(65535);
    const [valueStep, setValueStep] = useState(1);
    const [validSelectOptions, setValidSelectOptions] = useState([]);

    useEffect(() => {
        if (!selectedParam) return;
        let metadataValue = {};
        if (window.paramList[selectedMavId] && window.paramList[selectedMavId][selectedParam]) {
            metadataValue = window.paramList[selectedMavId][selectedParam].metaData || {};
        }
        setParamMetadataValue(metadataValue);

        const bitmaskValue = !!(metadataValue.bitmask && Object.keys(metadataValue.bitmask).length > 0);
        const selectValue = !!(metadataValue.values && Object.keys(metadataValue.values).length > 0);
        const normalValue =
            !!(metadataValue.fields && Object.keys(metadataValue.fields).length > 0) &&
            !bitmaskValue &&
            !selectValue;

        setIsBitmaskValue(bitmaskValue);
        setIsSelectValue(selectValue);
        setIsNormalValue(normalValue);

        const paramType = window.paramList[selectedMavId]?.[selectedParam]?.type;
        const defaultRange = getDefaultRangeByParamType(paramType);
        let min = defaultRange.min;
        let max = defaultRange.max;
        let step = defaultRange.step;
        if (metadataValue.fields) {
            if (metadataValue.fields.Range) {
                const range = metadataValue.fields.Range.split(' ');
                min = parseFloat(range[0]);
                max = parseFloat(range[1]);
            }
            if (metadataValue.fields.Increment) {
                step = parseFloat(metadataValue.fields.Increment);
            }
        }
        setMinValue(min);
        setMaxValue(max);
        setValueStep(step);

        const validOptions = [];
        if (selectValue) {
            Object.keys(metadataValue.values).forEach((key) => {
                validOptions.push(parseFloat(metadataValue.values[key]));
            });
        }
        setValidSelectOptions(validOptions);
        setManualInputValue(String(paramValue ?? ''));
        setError(false);
        setHelperText('');
    }, [selectedParam]);

    useEffect(() => {
        if (!selectedParam) return;
        setManualInputValue(String(paramValue ?? ''));
    }, [paramValue, selectedParam]);

    const handleBitmaskCheckboxChange = (bit) => {
        const bitValue = 1 << parseInt(bit);
        setParamValue(paramValue ^ bitValue);
    };

    const handleSelectChange = (event) => {
        const value = Number(event.target.value);
        setParamValue(value);
        validateValue(value);
    };

    const validateValue = (value) => {
        if (!Number.isFinite(value)) {
            setError(true);
            setHelperText('Please enter a valid number');
            return;
        }

        const formatErr = checkParamValue(value, window.paramList[selectedMavId][selectedParam].type);
        if (formatErr) {
            setError(true);
            setHelperText(formatErr);
        } else if (value < minValue || value > maxValue) {
            setError(true);
            setHelperText(`Value must be between ${minValue} and ${maxValue}`);
        } else {
            setError(false);
            setHelperText('');
        }
    };

    const handleManualInputChange = (event) => {
        const value = event.target.value.trim();
        setManualInputValue(event.target.value);

        // Allow normal number typing flow, including temporary '-', '.', and '-.' states.
        if (value === '' || value === '-' || value === '.' || value === '-.') {
            setError(true);
            setHelperText('Please enter a valid number');
            return;
        }

        if (!/^-?\d*(\.\d*)?$/.test(value)) {
            setError(true);
            setHelperText('Please enter a valid number');
            return;
        }

        const numericValue = Number(value);
        setParamValue(numericValue);
        validateValue(numericValue);
    };

    if (!selectedParam || !params || !params[selectedParam]) {
        return (
            <Box
                sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.disabled',
                }}
            >
                <Typography variant="caption">Pick a parameter from the right list</Typography>
            </Box>
        );
    }

    const fields = paramMetadataValue?.fields || {};
    const unitText = fields.Units || fields.UnitText || '';

    const metaItems = [];
    if (unitText) metaItems.push(['Unit', unitText]);
    if (fields.Range) metaItems.push(['Range', fields.Range.split(' ').join(', ')]);
    if (fields.Increment) metaItems.push(['Step', fields.Increment]);
    if (fields.RebootRequired) metaItems.push(['Reboot', 'Required']);

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
                minHeight: 0,
            }}
        >
            {/* Left: editor */}
            <Stack spacing={1.25} sx={{ minWidth: 0 }}>
                {/* Value editor */}
                {isSelectValue ? (
                    <Stack spacing={1}>
                        <Select
                            value={validSelectOptions.includes(Number(paramValue)) ? Number(paramValue) : ''}
                            onChange={handleSelectChange}
                            displayEmpty
                            sx={{ minWidth: 0 }}
                        >
                            {!validSelectOptions.includes(Number(paramValue)) && (
                                <MenuItem value="" disabled>
                                    {`Current: ${paramValue} (not in list)`}
                                </MenuItem>
                            )}
                            {Object.keys(paramMetadataValue.values).map((key, idx) => (
                                <MenuItem key={idx} value={parseFloat(paramMetadataValue.values[key])}>
                                    {key}
                                </MenuItem>
                            ))}
                        </Select>
                        <TextField
                            label="Manual value"
                            value={manualInputValue}
                            onChange={handleManualInputChange}
                            error={error}
                            helperText={helperText || 'Type a numeric value manually if not in dropdown options'}
                            inputProps={{
                                inputMode: 'decimal',
                                max: maxValue,
                                min: minValue,
                                step: valueStep,
                            }}
                        />
                    </Stack>
                ) : (
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                        <TextField
                            label={selectedParam}
                            value={manualInputValue}
                            onChange={handleManualInputChange}
                            type="number"
                            error={error}
                            helperText={helperText || ' '}
                            inputProps={{
                                inputMode: 'decimal',
                                max: maxValue,
                                min: minValue,
                                step: valueStep,
                            }}
                            sx={{ flex: 1, minWidth: 0 }}
                        />
                    </Stack>
                )}

                {/* Bitmask */}
                {isBitmaskValue && (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                            rowGap: 0,
                            columnGap: 1,
                            maxHeight: 240,
                            overflow: 'auto',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 0.5,
                        }}
                    >
                        {Object.keys(paramMetadataValue.bitmask).map((key, idx) => {
                            const bitPosition = parseInt(paramMetadataValue.bitmask[key]);
                            const bitValue = 1 << bitPosition;
                            const bitMatched = (paramValue & bitValue) === bitValue;
                            return (
                                <FormControlLabel
                                    key={idx}
                                    control={
                                        <Checkbox
                                            checked={bitMatched}
                                            onChange={() => handleBitmaskCheckboxChange(bitPosition)}
                                        />
                                    }
                                    label={key}
                                    sx={{ m: 0 }}
                                />
                            );
                        })}
                    </Box>
                )}

                {/* Metadata chips */}
                {isNormalValue && metaItems.length > 0 && (
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {metaItems.map(([label, value]) => (
                            <Chip
                                key={label}
                                size="small"
                                variant="outlined"
                                label={`${label}: ${value}`}
                            />
                        ))}
                    </Stack>
                )}
            </Stack>

            {/* Right: docs */}
            <Stack spacing={0.75} sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {paramMetadataValue?.humanName || selectedParam}
                </Typography>
                <Divider />
                <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap' }}
                >
                    {paramMetadataValue?.documentation || 'No documentation available.'}
                </Typography>
            </Stack>
        </Box>
    );
};

export default ParameterValueEditor;
